// EuroView checkout: a thin landing page invoked by the EuroView app.
//
// Expected URL (signed by the create-order Edge Function):
//   /checkout?orderId=...&token=...&package=M6&amount=5000&currency=eur
//             &uid=<supabase-user-id>&email=<user-email>&sig=<hmac>

import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import StripeCheckout from '../edi/StripeCheckout/StripeCheckout';
import NowPaymentsCheckout from '../edi/NowPaymentsCheckout/NowPaymentsCheckout';

// ─── Brand SVG icons ────────────────────────────────────────────────────────

const VisaBadge = () => (
  <svg width="44" height="16" viewBox="0 0 44 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="44" height="16" rx="3" fill="#1A1F71" />
    <text x="22" y="12" textAnchor="middle" fill="#FFFFFF" fontFamily="Arial,sans-serif"
      fontWeight="800" fontStyle="italic" fontSize="10" letterSpacing="1">VISA</text>
  </svg>
);

const MastercardBadge = () => (
  <svg width="36" height="24" viewBox="0 0 36 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="13" cy="12" r="11" fill="#EB001B" />
    <circle cx="23" cy="12" r="11" fill="#F79E1B" />
    <path d="M18 3.8a11 11 0 0 1 0 16.4A11 11 0 0 1 18 3.8z" fill="#FF5F00" />
  </svg>
);

const BitcoinBadge = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="12" fill="#F7931A" />
    <path fill="#fff" d="M16.6 10.8c.3-1.8-1.1-2.7-2.9-3.3l.6-2.4-1.4-.4-.6 2.3-1.1-.3.6-2.3-1.4-.3-.6 2.4-2.9-.8-.3 1.5s1 .3 1 .3c.5.1.6.5.6.8l-1.7 6.8c-.1.2-.3.5-.8.4l-1-.3-.7 1.6 2.7.7-.6 2.3 1.4.4.6-2.4 1.1.3-.6 2.3 1.4.4.6-2.4c2.4.5 4.2.3 5-1.9.6-1.8-.1-2.9-1.3-3.5 1-.2 1.7-.9 1.8-2.1zm-3.2 4.5c-.5 1.8-3.4.8-4.3.6l.8-3.1c.9.2 3.9.7 3.5 2.5zm.5-4.5c-.4 1.6-3 .8-3.8.6l.7-2.8c.8.2 3.4.6 3.1 2.2z" />
  </svg>
);

const TetherBadge = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="12" fill="#26A17B" />
    <path fill="#fff" d="M13.3 10V8.4h3.4V6.2H7.3v2.2h3.4V10c-3 .2-5.2 1-5.2 2s2.2 1.8 5.2 2V18h2.6v-4c3-.2 5.2-1 5.2-2s-2.2-1.8-5.2-2zm0 3.4v-.1c0 0-.9.1-1.3.1-.4 0-1.2-.1-1.3-.1v.1c-2.8-.1-4.9-.8-4.9-1.4s2.1-1.3 4.9-1.4v1l1.3.1 1.3-.1v-1c2.8.1 4.9.8 4.9 1.4s-2.1 1.3-4.9 1.4z" />
  </svg>
);

const EthBadge = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="12" fill="#627EEA" />
    <path fill="#fff" fillOpacity=".6" d="M12.001 4v6.5l5.5 2.455z" />
    <path fill="#fff" d="M12.001 4 6.5 12.955l5.501-2.455z" />
    <path fill="#fff" fillOpacity=".6" d="M12.001 16.477V20l5.503-7.617z" />
    <path fill="#fff" d="M12.001 20v-3.523L6.5 12.383z" />
    <path fill="#fff" fillOpacity=".2" d="m12.001 15.43 5.5-3.23-5.5-2.446z" />
    <path fill="#fff" fillOpacity=".6" d="m6.5 12.2 5.501 3.23V9.754z" />
  </svg>
);

const LockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// ─── Constants ───────────────────────────────────────────────────────────────

const PACKAGE_LABELS = {
  M1: { name: '1 Month', months: 1 },
  M3: { name: '3 Months', months: 3 },
  M6: { name: '6 Months', months: 6 },
  M12: { name: '12 Months', months: 12 },
  SILVER12: { name: 'Silver — 12 Months', months: 12 },
  PREMIUM12: { name: 'Premium — 12 Months', months: 12 },
};

const ALLOWED_CURRENCIES = ['eur', 'usd'];
const MAX_AMOUNT_CENTS = 100000;

function readParams(search) {
  const q = new URLSearchParams(search);
  const get = (k) => (q.get(k) || '').trim();
  return {
    orderId: get('orderId'),
    token: get('token'),
    pkg: get('package'),
    amountStr: get('amount'),
    currency: get('currency').toLowerCase() || 'eur',
    uid: get('uid'),
    email: get('email'),
    deviceCount: get('deviceCount') || '1',
    sig: get('sig'),
    successUrl: get('success_url'),
    provider: get('provider'),
    billing: get('billing').toLowerCase(),
    interval: get('interval'),
  };
}

/** "month" / "3 months" / "year" — used in every recurring disclosure. */
function intervalLabel(months) {
  if (months === 1) return 'month';
  if (months === 12) return 'year';
  return `${months} months`;
}

// ─── Method selector card ────────────────────────────────────────────────────

const MethodCard = ({ selected, onClick, title, subtitle, icons }) => {
  const base = {
    border: selected ? '2px solid #111827' : '1.5px solid #e5e7eb',
    borderRadius: 12,
    padding: '16px 18px',
    cursor: 'pointer',
    background: selected ? '#111827' : '#ffffff',
    transition: 'border-color 0.15s, background 0.15s, box-shadow 0.15s',
    boxShadow: selected ? '0 4px 16px rgba(17,24,39,0.14)' : 'none',
    userSelect: 'none',
    flex: 1,
    minWidth: 160,
  };

  return (
    <div style={base} onClick={onClick} role="button" tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: selected ? '#fff' : '#111827', marginBottom: 2 }}>
            {title}
          </div>
          <div style={{ fontSize: 11, color: selected ? 'rgba(255,255,255,0.55)' : '#9ca3af', lineHeight: 1.4 }}>
            {subtitle}
          </div>
        </div>
        <div style={{
          width: 18, height: 18, borderRadius: '50%', flexShrink: 0, marginTop: 2,
          border: selected ? '5px solid #fff' : '1.5px solid #d1d5db',
          background: selected ? '#111827' : '#fff',
          transition: 'border 0.15s',
        }} />
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {icons}
      </div>
    </div>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

const EuroViewCheckout = () => {
  const { search } = useLocation();

  const providerParam = useMemo(() => {
    const q = new URLSearchParams(search);
    return (q.get('provider') || '').toLowerCase();
  }, [search]);

  const [method, setMethod] = useState(providerParam === 'nowpayments' ? 'crypto' : 'card');

  const { params, error, amountCents, priceMajor, pkgInfo } = useMemo(() => {
    const params = readParams(search);
    const required = ['orderId', 'token', 'pkg', 'amountStr', 'currency', 'uid', 'sig'];
    for (const k of required) {
      if (!params[k]) return { params, error: 'Invalid checkout link. Please go back and try again.' };
    }
    if (!ALLOWED_CURRENCIES.includes(params.currency)) {
      return { params, error: 'Unsupported currency.' };
    }
    const amountCents = Number(params.amountStr);
    if (!Number.isInteger(amountCents) || amountCents <= 0 || amountCents > MAX_AMOUNT_CENTS) {
      return { params, error: 'Invalid amount.' };
    }
    const pkgInfo = PACKAGE_LABELS[params.pkg] || { name: params.pkg, months: null };
    return { params, amountCents, priceMajor: amountCents / 100, pkgInfo };
  }, [search]);

  const sym = params && params.currency === 'usd' ? '$' : '€';

  // Auto-renewing checkout. Crypto has no mandate mechanism, so recurring is
  // card-only — the method selector is hidden rather than offering a crypto tab
  // that silently would not renew.
  const intervalMonths = Number(params && params.interval);
  const isRecurring =
    !error &&
    params.billing === 'recurring' &&
    [1, 3, 6, 12].includes(intervalMonths);

  if (error) {
    return (
      <div style={S.page}>
        <div style={S.errorBox}>{error}</div>
      </div>
    );
  }

  const metadata = {
    euroview: 'true',
    order_id: params.orderId,
    token: params.token,
    package: params.pkg,
    amount_cents: String(amountCents),
    currency: params.currency,
    user_id: params.uid,
    device_count: params.deviceCount,
    sig: params.sig,
    ...(isRecurring ? { interval_months: String(intervalMonths) } : {}),
  };

  const successCtaUrl =
    params.successUrl && /^https?:\/\//i.test(params.successUrl)
      ? params.successUrl
      : 'https://euroview-iptv.com/al/account';

  return (
    <div style={S.page}>

      {/* ── Header ─────────────────────────────────── */}
      <div style={S.header}>
        <div style={S.logoRow}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#111827"
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <span style={{ fontSize: 18, fontWeight: 700, color: '#111827', letterSpacing: '-0.3px' }}>
            EuroView
          </span>
        </div>
        <div style={S.headerSub}>
          Secure checkout &nbsp;·&nbsp; Powered by pro-paynow.com
        </div>
      </div>

      {/* ── Main card ──────────────────────────────── */}
      <div style={S.card}>

        {/* Order summary */}
        <div style={S.summaryRow}>
          <div>
            <div style={S.pkgName}>{pkgInfo.name}</div>
            {/* Was `Auto-renewing every ${intervalLabel(intervalMonths)}` in
                recurring mode; dropped 20 Aug 2026 at the site owner's request.
                The recurring terms are one click away at the payment step —
                see the "Subscription terms" toggle in StripePaymentForm. */}
            <div style={S.pkgSub}>Streaming subscription</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={S.price}>{sym}{priceMajor.toFixed(2)}</div>
            {/* The interval was appended here as "EUR / month" in recurring
                mode; dropped 21 Aug 2026 at the site owner's request. The
                currency alone stays, since the amount needs a unit. */}
            <div style={S.currencyTag}>{params.currency.toUpperCase()}</div>
          </div>
        </div>

        {/* REMOVED 20 Aug 2026 at the site owner's request: the recurring-terms
            callout that sat here. The owner's position is that auto-renewal is
            already disclosed and ticked on euroview-iptv.com before the customer
            reaches this page, so repeating it here is redundant.

            Kept in comment form because removing recurring disclosure from the
            payment step itself is the part Stripe's subscription rules and the
            EU Consumer Rights Directive both attach to, and this is the block to
            restore if that ever needs reversing.

        {isRecurring && (
          <div
            style={{
              margin: '0 0 18px',
              padding: '12px 14px',
              background: '#fff7ed',
              border: '1px solid #fdba74',
              borderRadius: 10,
              fontSize: 12.5,
              lineHeight: 1.55,
              color: '#7c2d12',
            }}>
            <div style={{ fontWeight: 700, marginBottom: 3 }}>
              Subscription — renews automatically
            </div>
            {sym}{priceMajor.toFixed(2)} is charged today, then {sym}{priceMajor.toFixed(2)} every{' '}
            {intervalLabel(intervalMonths)} until you cancel. Cancel any time from your EuroView
            profile; your access always runs to the end of the period you have paid for.
          </div>
        )}
        */}

        {/* Method selector — recurring is card-only (crypto cannot auto-renew) */}
        {!isRecurring && (
          <div style={S.selectorSection}>
            <div style={S.selectorLabel}>Payment method</div>
            <div style={S.methodRow}>

              <MethodCard
                selected={method === 'card'}
                onClick={() => setMethod('card')}
                title="Debit / Credit Card"
                subtitle="Pay by card, instant"
                icons={<><VisaBadge /><MastercardBadge /></>}
              />

              <MethodCard
                selected={method === 'crypto'}
                onClick={() => setMethod('crypto')}
                title="Cryptocurrency"
                subtitle="BTC, USDT, ETH and more"
                icons={<><BitcoinBadge /><TetherBadge /><EthBadge /></>}
              />

            </div>
          </div>
        )}

        {/* Form area */}
        <div style={S.formArea}>
          {(isRecurring || method === 'card') && (
            <StripeCheckout
              price={priceMajor}
              basePrice={priceMajor}
              tax={0}
              currency={params.currency}
              description={
                isRecurring
                  ? `EuroView — ${pkgInfo.name} (auto-renewing)`
                  : `EuroView — ${pkgInfo.name}`
              }
              autoCheckTerms
              metadata={metadata}
              prefillEmail={params.email}
              hideTaxBreakdown
              successCtaUrl={successCtaUrl}
              successCtaLabel="Back to my account"
              subscriptionMode={isRecurring}
              intervalMonths={intervalMonths}
            />
          )}
          {!isRecurring && method === 'crypto' && (
            <NowPaymentsCheckout
              price={priceMajor}
              basePrice={priceMajor}
              tax={0}
              currency={params.currency}
              description={`EuroView — ${pkgInfo.name}`}
              autoCheckTerms
              metadata={metadata}
              prefillEmail={params.email}
              hideTaxBreakdown
              successCtaUrl={successCtaUrl}
              successCtaLabel="Back to my account"
            />
          )}
        </div>

      </div>

      {/* ── Footer trust row ───────────────────────── */}
      <div style={S.trustRow}>
        <span style={S.trustItem}><LockIcon /> 256-bit TLS</span>
        <span style={S.trustSep} />
        <span style={S.trustItem}>PCI DSS Compliant</span>
        <span style={S.trustSep} />
        <span style={S.trustItem}>Stripe Verified</span>
      </div>

    </div>
  );
};

export default EuroViewCheckout;

// ─── Style map ───────────────────────────────────────────────────────────────

const S = {
  page: {
    minHeight: '100vh',
    background: '#f7f8fa',
    padding: '36px 16px 48px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 20,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  header: {
    textAlign: 'center',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'center',
    marginBottom: 6,
  },
  headerSub: {
    fontSize: 13,
    color: '#9ca3af',
    letterSpacing: '0.01em',
  },
  card: {
    width: '100%',
    maxWidth: 520,
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 2px 12px rgba(17,24,39,0.06)',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #f3f4f6',
    background: '#fafafa',
  },
  pkgName: {
    fontSize: 16,
    fontWeight: 700,
    color: '#111827',
    margin: 0,
  },
  pkgSub: {
    fontSize: 12,
    color: '#9ca3af',
    marginTop: 3,
  },
  price: {
    fontSize: 22,
    fontWeight: 800,
    color: '#111827',
    lineHeight: 1,
  },
  currencyTag: {
    display: 'inline-block',
    marginTop: 5,
    fontSize: 11,
    fontWeight: 600,
    color: '#4f46e5',
    background: '#eef2ff',
    borderRadius: 4,
    padding: '2px 7px',
  },
  selectorSection: {
    padding: '22px 24px 0',
  },
  selectorLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: 12,
  },
  methodRow: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
  formArea: {
    padding: '20px 24px 24px',
    borderTop: '1px solid #f3f4f6',
    marginTop: 20,
  },
  errorBox: {
    maxWidth: 520,
    width: '100%',
    padding: '14px 18px',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#b91c1c',
    borderRadius: 12,
    fontSize: 14,
  },
  trustRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  trustItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    color: '#9ca3af',
  },
  trustSep: {
    width: 3,
    height: 3,
    borderRadius: '50%',
    background: '#d1d5db',
    display: 'inline-block',
  },
};
